import React, { useMemo } from 'react';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { useAppSelector } from 'app/hooks';
import { allowDecimalByCountry } from 'user/selectors';
import { getNominalWeightByCropId } from 'crops/selectors';
import { getActiveCropDfpUuid } from 'current/selectors';
import { getMessage } from 'util/locale';
import { convertForLocaleScalar, inferUnit } from 'util/unitConversion';
import { formatAvgYield, formatNumberRoundToNearest } from 'util/formatters/numberFormatter';
import { ZoneLabel } from './CustomCells';

import type { PrescriptionZone } from 'mapping/types';

interface ZonesTableProps {
  onClickZone: (zoneId: string) => void;
  seedingRateBins: [number, number][];
  selectedZoneId: string | null;
  zones: PrescriptionZone[];
}

const baseString = 'review-scripts-zones-table';
export function ZonesTable({ onClickZone, selectedZoneId, seedingRateBins, zones }: ZonesTableProps) {
  const isDecimalCountry = useAppSelector(allowDecimalByCountry);
  const activeCropUuid = useAppSelector(getActiveCropDfpUuid);
  const cropNominalWeight = useAppSelector((state) => getNominalWeightByCropId(state, activeCropUuid));

  const sortedZones = useMemo(() => zones.toSorted((a, b) => b.targetYield.v - a.targetYield.v), [zones]);

  const columns: ColumnDef<PrescriptionZone>[] = useMemo(() => [
    {
      accessorFn: (_, index: number) => index,
      header: getMessage('zone'),
      cell: ({ row }) => (
        <ZoneLabel
          rowIndex={row.index}
          seedingRate={row.original.seedingRate}
          seedingRateBins={seedingRateBins}
        />
      )
    },
    {
      accessorFn: ({ targetYield }) => targetYield.v,
      header: `${getMessage('target-yield')} (${inferUnit('area-density.yield').symbol.quantitySpecific})`,
      cell: ({ row }) => formatAvgYield(convertForLocaleScalar({
        quantity: row.original.targetYield.v,
        unit: row.original.targetYield.u,
        unitType: 'area-density.yield',
        cropNominalWeight
      }), isDecimalCountry)
    },
    {
      accessorFn: ({ seedingRate }) => seedingRate.v,
      header: `${getMessage('rate')} (${inferUnit('seed-density').symbol.quantitySpecific})`,
      cell: ({ row }) => formatNumberRoundToNearest(convertForLocaleScalar({
        quantity: row.original.seedingRate.v,
        unit: row.original.seedingRate.u,
        unitType: 'seed-density',
        cropNominalWeight
      }), 1).toLocaleString()
    }
  ], [cropNominalWeight, isDecimalCountry, seedingRateBins]);

  const zonesTable = useReactTable({
    columns,
    data: sortedZones,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <table className={baseString} data-test-id={baseString}>
      <thead>
        {zonesTable.getHeaderGroups().map((hg) => (
          <tr key={hg.id}>
            {hg.headers.map((header) => (
              <th key={header.id} data-test-id={`header-${header.id}`}>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {zonesTable.getRowModel().rows.map((row) => (
          <tr
            key={row.original.id}
            onClick={() => onClickZone(row.original.id)}
            data-zone-isSelected={selectedZoneId === row.original.id}
          >
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id} data-test-id={`cell-${cell.id}`}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
